import React, { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box,
    Typography,
    Grid,
    CircularProgress,
    Alert,
    Autocomplete,
    TextField,
    Chip,
    Paper,
    Stack,
    alpha,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FlagIcon from '@mui/icons-material/Flag';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { fetchUserKpiPeriods, fetchUserKpiDetails } from '../redux.actions';
import { fetchEmployeePreview } from '../redux.actions';
import {
    selectUserKpiPeriods,
    selectUserKpiDetails,
    selectDashboardLoading,
    selectDashboardError
} from '../redux.selectors';
import { selectEmployeePreview } from '../redux.selectors';

import ScoreChart from './ScoreChart';
import GoalsOverview from './GoalsOverview';
import OrganizationalInfo from './OrganizationalInfo';
import KpiCharts from './KpiCharts';


const Dashboard = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const location = useLocation();
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [remarksModalOpen, setRemarksModalOpen] = useState(false);

    // Expose dashboard update for Team Member card view icon
    window.dispatchDashboardUpdate = (newUserId) => {
        dispatch(fetchUserKpiPeriods(newUserId));
        setSelectedPeriod(null);
    };

    // Parse userId and kpiPeriod from URL
    const searchParams = new URLSearchParams(location.search);
    const userId = searchParams.get('userId');
    const urlPeriodId = searchParams.get('kpiPeriod');

    const periods = useSelector(selectUserKpiPeriods);
    const kpiDetails = useSelector(selectUserKpiDetails);
    const loading = useSelector(selectDashboardLoading);
    const error = useSelector(selectDashboardError);
    const employeePreview = useSelector(selectEmployeePreview);

    // Flatten all remarks with goal info, sorted by date (latest first)
    const allRemarks = useMemo(() => {
        if (!kpiDetails?.goals) return [];
        const remarks = [];
        kpiDetails.goals.forEach(goal => {
            const goalName = goal.goal?.name || goal.name || 'Unknown Goal';
            (goal.scores || []).forEach(score => {
                const remarkText = score?.remarks || score?.remark || score?.comments;
                if (remarkText && typeof remarkText === 'string' && remarkText.trim()) {
                    remarks.push({
                        ...score,
                        goalName,
                        goalId: goal.id,
                        remarkText
                    });
                }
            });
        });
        return remarks.sort((a, b) => {
            const dateA = a.scoreDate || a.updatedAt || '';
            const dateB = b.scoreDate || b.updatedAt || '';
            return new Date(dateB) - new Date(dateA);
        });
    }, [kpiDetails?.goals]);

    const latestRemarks = allRemarks.slice(0, 3);
    const hasMoreRemarks = allRemarks.length > 3;

    useEffect(() => {
        // Fetch KPI periods for user or current user
        dispatch(fetchUserKpiPeriods(userId));
        // Fetch employee preview data
        dispatch(fetchEmployeePreview());
    }, [dispatch, userId]);

    useEffect(() => {
        // Select period from URL or default to current period
        if (Array.isArray(periods) && periods.length > 0 && !selectedPeriod) {
            let initialPeriod = null;
            if (urlPeriodId) {
                initialPeriod = periods.find(p => String(p.id) === String(urlPeriodId));
            }
            if (!initialPeriod) {
                initialPeriod = periods.find(period => period.isCurrent) || periods[0];
            }
            setSelectedPeriod(initialPeriod);
        }
    }, [periods, selectedPeriod, urlPeriodId]);

    // Ensure selectedPeriod is always valid when periods change
    useEffect(() => {
        if (Array.isArray(periods) && periods.length > 0 && selectedPeriod) {
            const periodExists = periods.some(p => p.id === selectedPeriod.id);
            if (!periodExists) {
                // Selected period no longer exists, reset to current or first period
                const fallbackPeriod = periods.find(period => period.isCurrent) || periods[0];
                setSelectedPeriod(fallbackPeriod);
            }
        }
    }, [periods, selectedPeriod]);

    useEffect(() => {
        // Fetch KPI details for selected period and user
        if (selectedPeriod) {
            dispatch(fetchUserKpiDetails(selectedPeriod.id, userId));
        }
    }, [dispatch, selectedPeriod, userId]);

    const handlePeriodChange = (event, newValue) => {
        setSelectedPeriod(newValue);
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        // Handle structured error object
        const errorMessage = typeof error === 'object' && error.message
            ? error.message
            : error;

        const errorDetails = typeof error === 'object' && error.statusCode
            ? error
            : null;

        return (
            <Alert severity="error" sx={{ m: 2 }}>
                <Box>
                    <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>
                        {errorMessage}
                    </Typography>
                    {errorDetails && (
                        <Box component="pre" sx={{
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            mt: 1,
                            p: 1,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            overflow: 'auto'
                        }}>
                            {JSON.stringify({
                                statusCode: errorDetails.statusCode,
                                message: errorDetails.message,
                                error: errorDetails.error
                            }, null, 2)}
                        </Box>
                    )}
                </Box>
            </Alert>
        );
    }

    // Calculate KPI metrics
    const calculateMetrics = () => {
        if (!kpiDetails || !kpiDetails.goals) return null;

        const goals = kpiDetails.goals;
        let totalWeightedScore = 0;
        let totalWeight = 0;
        let goalsWithScores = 0;

        goals.forEach(goal => {
            const weight = Number(goal.weight) || 0;
            if (goal.scores?.length > 0) {
                goalsWithScores++;
                const avgScore = goal.scores.reduce((sum, s) => sum + Number(s.score), 0) / goal.scores.length;
                totalWeightedScore += (avgScore / 5) * weight;
                totalWeight += weight;
            }
        });

        const avgOn5Scale = totalWeight > 0 ? (totalWeightedScore / totalWeight) * 5 : 0;
        const fractional = avgOn5Scale - Math.floor(avgOn5Scale);
        const finalAvg = fractional >= 0.9 ? Math.ceil(avgOn5Scale) : avgOn5Scale;
        const overallScore = totalWeight > 0 ? (finalAvg / 5) * 100 : 0;
        const completionRate = goals.length > 0 ? (goalsWithScores / goals.length) * 100 : 0;

        return {
            overallScore: Math.round(overallScore * 10) / 10,
            completionRate: Math.round(completionRate),
            totalGoals: goals.length,
            goalsWithScores
        };
    };

    const metrics = calculateMetrics();

    // Compute latest date average: for the most recent score date, take each goal's latest score on that date
    const computeLatestDateAverage = () => {
        if (!kpiDetails || !kpiDetails.goals) return null;
        const perDatePerGoal = {}; // dateKey -> goalId -> { score, ts }

        kpiDetails.goals.forEach(goal => {
            if (!goal.scores || goal.scores.length === 0) return;
            goal.scores.forEach(score => {
                const dateKey = score.scoreDate
                    ? dayjs(score.scoreDate).format('YYYY-MM-DD')
                    : dayjs(score.updatedAt || score.createdAt).format('YYYY-MM-DD');

                if (!perDatePerGoal[dateKey]) perDatePerGoal[dateKey] = {};

                const ts = new Date(score.updatedAt || score.createdAt).getTime();
                const existing = perDatePerGoal[dateKey][goal.id];
                const numericScore = parseFloat(score.score || 0);

                // Keep the latest score entry for this goal on this date (by timestamp)
                if (!existing || ts > existing.ts) {
                    perDatePerGoal[dateKey][goal.id] = { score: numericScore, ts };
                }
            });
        });

        const dates = Object.keys(perDatePerGoal).sort((a, b) => new Date(b) - new Date(a));
        if (dates.length === 0) return null;

        const latest = dates[0];
        const values = Object.values(perDatePerGoal[latest]).map(v => v.score);
        if (values.length === 0) return null;

        const avg = values.reduce((s, v) => s + v, 0) / values.length;
        return { avg, date: latest, count: values.length };
    };

    const latestDateInfo = computeLatestDateAverage();

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box sx={{ 
                minHeight: '100vh',
                bgcolor: 'background.default',
                px: { xs: 1.5, sm: 2, md: 3 },
                py: { xs: 2, md: 3 }
            }}>
                {/* Header Section */}
                <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1 }}>
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            color: 'text.primary',
                            fontWeight: 700,
                            fontSize: { xs: '1.75rem', sm: '2.125rem' }
                        }}
                    >
                        Performance Dashboard
                    </Typography>
                </Box>

                {/* Period Selector */}
                <Paper 
                    elevation={0} 
                    sx={(theme) => ({ 
                        mb: 3, 
                        p: 3, 
                        borderRadius: 3,
                        bgcolor: 'background.paper',
                        border: `1px solid ${theme.palette.divider}`
                    })}
                >
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Select KPI Period
                    </Typography>
                    <Autocomplete
                        options={Array.isArray(periods) ? periods : []}
                        getOptionLabel={(option) => `${option?.name || 'Unknown'} (${dayjs(option?.startDate).format('MMM YYYY')} - ${dayjs(option?.endDate).format('MMM YYYY')})`}
                        value={selectedPeriod}
                        onChange={handlePeriodChange}
                        loading={loading}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Choose a period to view KPI details"
                                size="medium"
                                fullWidth
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2
                                    }
                                }}
                            />
                        )}
                        renderOption={(props, option) => (
                            <Box component="li" {...props}>
                                <Stack spacing={0.5} sx={{ py: 0.5 }}>
                                    <Typography variant="body1" fontWeight="medium">
                                        {option?.name || 'Unknown'}
                                    </Typography>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="caption" color="text.secondary">
                                            {dayjs(option?.startDate).format('MMM DD, YYYY')} - {dayjs(option?.endDate).format('MMM DD, YYYY')}
                                        </Typography>
                                        {option?.isCurrent && (
                                            <Chip
                                                label="Current"
                                                size="small"
                                                color="primary"
                                                sx={{ height: 20 }}
                                            />
                                        )}
                                    </Box>
                                </Stack>
                            </Box>
                        )}
                    />
                </Paper>

                {selectedPeriod && kpiDetails && metrics && (
                    <>
                        {/* Key Metrics Cards */}
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={0}
                                    sx={(theme) => ({
                                        p: 3,
                                        borderRadius: 3,
                                        bgcolor: 'background.paper',
                                        border: `1px solid ${theme.palette.divider}`,
                                        height: '140px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    })}
                                >
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <EmojiEventsIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                            Overall Score
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.primary.main }}>
                                        {metrics.totalGoals === 0 ? 'N/A' : `${metrics.overallScore}%`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Performance (out of 100%)
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={0}
                                    sx={(theme) => ({
                                        p: 3,
                                        borderRadius: 3,
                                        bgcolor: 'background.paper',
                                        border: `1px solid ${theme.palette.divider}`,
                                        height: '140px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    })}
                                >
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <TrendingUpIcon sx={{ fontSize: 28, color: theme.palette.success.main }} />
                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                            Latest Score
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.success.main }}>
                                        {latestDateInfo ? `${latestDateInfo.avg.toFixed(1)}/5` : 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {latestDateInfo ? dayjs(latestDateInfo.date).format('MMM DD, YYYY') : 'No scores yet'}
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={0}
                                    sx={(theme) => ({
                                        p: 3,
                                        borderRadius: 3,
                                        bgcolor: 'background.paper',
                                        border: `1px solid ${theme.palette.divider}`,
                                        height: '140px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    })}
                                >
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <FlagIcon sx={{ fontSize: 28, color: theme.palette.warning.main }} />
                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                            Total Goals
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: theme.palette.warning.main }}>
                                        {metrics.totalGoals}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Assigned Goals
                                    </Typography>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Paper
                                    elevation={0}
                                    sx={(theme) => ({
                                        p: 3,
                                        borderRadius: 3,
                                        bgcolor: 'background.paper',
                                        border: `1px solid ${theme.palette.divider}`,
                                        height: '140px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center'
                                    })}
                                >
                                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                                        <CalendarTodayIcon sx={{ fontSize: 28, color: '#9c27b0' }} />
                                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                            Active Period
                                        </Typography>
                                    </Box>
                                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: '#9c27b0' }}>
                                        {selectedPeriod?.name || 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {selectedPeriod ? `${dayjs(selectedPeriod.startDate).format('MMM YYYY')} - ${dayjs(selectedPeriod.endDate).format('MMM YYYY')}` : 'No period selected'}
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>

                        {/* Main Content Grid */}
                        <Grid container spacing={3}>
                            {/* Left Column - Employee Info */}
                            <Grid item xs={12} lg={4}>
                                <OrganizationalInfo kpiDetails={kpiDetails} employeePreview={employeePreview} />
                            </Grid>

                            {/* Right Column - Score Chart */}
                            <Grid item xs={12} lg={8}>
                                <ScoreChart kpiDetails={kpiDetails} />
                            </Grid>
                        </Grid>

                        {/* Full Width - Goal Remarks */}
                        {kpiDetails?.goals && (
                            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, mt: 3 }}>
                                        <Typography variant="h6" fontWeight={600} sx={{ mb: 3 }}>
                                            Goal Remarks
                                        </Typography>

                                        {allRemarks.length === 0 ? (
                                            <Box sx={{ textAlign: 'center', py: 6 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    No remarks available for any goals
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <>
                                                <Stack direction="row" spacing={2} sx={{ overflowX: 'auto', pb: 1 }}>
                                                    {latestRemarks.map((score, scoreIndex) => (
                                                        <Box key={scoreIndex} sx={{
                                                            p: 3,
                                                            borderRadius: 2,
                                                            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.08) : alpha(theme.palette.divider, 0.04),
                                                            border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.5)}`,
                                                            minWidth: 280,
                                                            flex: '1 1 0',
                                                            transition: 'all 0.2s ease-in-out',
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.divider, 0.12),
                                                                borderColor: theme.palette.divider
                                                            }
                                                        }}>
                                                            <Typography variant="caption" fontWeight={600} color="primary" sx={{ mb: 1, display: 'block' }}>
                                                                {score.goalName}
                                                            </Typography>
                                                            <Box sx={{ mb: 2 }}>
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        color: 'text.primary',
                                                                        lineHeight: 1.6,
                                                                        fontSize: '0.95rem',
                                                                        fontStyle: 'italic'
                                                                    }}
                                                                >
                                                                    "{score.remarkText}"
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                pt: 2,
                                                                borderTop: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.3) : alpha(theme.palette.divider, 0.5)}`
                                                            }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography variant="body2" fontWeight={600} color="primary">
                                                                        Score:
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {parseFloat(score.score || 0).toFixed(1)}/5
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {score.scoreDate ? dayjs(score.scoreDate).format('MMM DD, YYYY') : 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Stack>

                                                {hasMoreRemarks && (
                                                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                                                        <Button
                                                            variant="outlined"
                                                            onClick={() => setRemarksModalOpen(true)}
                                                        >
                                                            Show More ({allRemarks.length - 3} more)
                                                        </Button>
                                                    </Box>
                                                )}
                                            </>
                                        )}
                                    </Paper>
                        )}

                        {/* All Remarks Modal */}
                        <Dialog
                            open={remarksModalOpen}
                            onClose={() => setRemarksModalOpen(false)}
                            maxWidth="md"
                            fullWidth
                        >
                            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="h6" fontWeight={600}>
                                    All Goal Remarks
                                </Typography>
                                <IconButton onClick={() => setRemarksModalOpen(false)} size="small">
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>
                            <DialogContent dividers>
                                {kpiDetails.goals.length > 0 ? (() => {
                                    const goalsWithRemarks = kpiDetails.goals.map((goal) => {
                                        const goalName = goal.goal?.name || goal.name || 'Unknown Goal';
                                        const remarks = goal.scores?.filter(score => {
                                            const remarkText = score?.remarks || score?.remark || score?.comments;
                                            return remarkText && typeof remarkText === 'string' && remarkText.trim();
                                        }) || [];

                                        if (remarks.length === 0) return null;

                                        return (
                                            <Box key={goal.id} sx={{ mb: 4 }}>
                                                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: 'text.primary' }}>
                                                    {goalName}
                                                </Typography>
                                                <Stack spacing={2}>
                                                    {remarks.map((score, scoreIndex) => (
                                                        <Box key={scoreIndex} sx={{
                                                            p: 3,
                                                            borderRadius: 2,
                                                            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.08) : alpha(theme.palette.divider, 0.04),
                                                            border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.5)}`
                                                        }}>
                                                            <Box sx={{ mb: 2 }}>
                                                                <Typography
                                                                    variant="body1"
                                                                    sx={{
                                                                        color: 'text.primary',
                                                                        lineHeight: 1.6,
                                                                        fontSize: '0.95rem',
                                                                        fontStyle: 'italic'
                                                                    }}
                                                                >
                                                                    "{score?.remarks || score?.remark || score?.comments || 'No remark text'}"
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                pt: 2,
                                                                borderTop: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.3) : alpha(theme.palette.divider, 0.5)}`
                                                            }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Typography variant="body2" fontWeight={600} color="primary">
                                                                        Score:
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                        {parseFloat(score.score || 0).toFixed(1)}/5
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {score.scoreDate ? dayjs(score.scoreDate).format('MMM DD, YYYY') : 'N/A'}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        );
                                    }).filter(Boolean);

                                    return goalsWithRemarks.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', py: 6 }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No remarks available for any goals
                                            </Typography>
                                        </Box>
                                    ) : goalsWithRemarks;
                                })() : (
                                    <Box sx={{ textAlign: 'center', py: 6 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            No goals available
                                        </Typography>
                                    </Box>
                                )}
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setRemarksModalOpen(false)}>Close</Button>
                            </DialogActions>
                        </Dialog>

                        {/* Full Width - KPI Charts */}
                        <Grid container spacing={3} sx={{ mt: '0' }}>
                            <Grid item xs={12} >
                                <KpiCharts kpiDetails={kpiDetails} />
                            </Grid>
                        </Grid>

                        {/* Full Width - Goals Overview */}
                        <Grid container spacing={3} sx={{ mt: '0' }}>
                            <Grid item xs={12}>
                                <GoalsOverview kpiDetails={kpiDetails} employeePreview={employeePreview} />
                            </Grid>
                        </Grid>
                    </>
                )}

                {!selectedPeriod && periods.length === 0 && (
                    <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            No KPI periods available. Please contact your administrator.
                        </Alert>
                    </Paper>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export default Dashboard;